import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { LinearProgress } from "@mui/material";
import { Placeholder } from "@/components/ui";
import { HeaderLabel } from "@/components/utils/DataTable";
import { GroupHeaderCell, layoutHeader, useGroupHeaderRow, type ColumnGroup } from "@/components/utils/groupedHeader";
import shared from "@/components/utils/shared.module.css";
import { PendingOrder, ConfirmDialogState, BulkEditFields, BulkCreateFields, StrategyFilter } from "../types";
import { RiskByCompanyId } from "../riskViolations";
import { OrderTable } from "./OrderTable";
import { RecommendationTable } from "./RecommendationTable";
import styles from "../OrderRecommendationList.module.css";

type SortCol = "company" | "days" | "decision";

interface ColSpec {
  key: string;
  header: string;
  group?: "holding" | "target" | "return";
  tooltip?: string;
  /** Figures: centred, in tabular digits. */
  numeric?: boolean;
  /** Short badges (Type, Decision): centred like the figures around them. */
  centered?: boolean;
  sort?: SortCol;
  /** Relative width. The table is fixed-layout and as wide as its card, so each
   *  column gets weight / Σweights of it. The weights are also pixel widths:
   *  in bulk edit, and on narrow screens, the table never shrinks below
   *  Σweights px, because the cells stop being usable narrower than that. */
  weight: number;
}

const GROUPS: ColumnGroup[] = [
  { key: "holding", label: "Holding %" },
  { key: "target", label: "Target Position" },
  { key: "return", label: "Annual Implied Return" },
];

const LAST_ORDER_TOOLTIP =
  "Target position from the last submitted order. Buy: max target % (Increase To) or increase by %. "
  + "Sell: target % (Sell To Target). Click the ⓘ on a row for the full sub-strategy breakdown.";

/**
 * The column order here must match the cell order in TableRow.
 *
 * Domestic shows PMS / AIF / IFSC holdings, Days (to trade a 1% position) and
 * Last Order. Global collapses the holdings into a single SGS column, drops
 * Days and Last Order, and adds the previous sessions' CIO instruction.
 */
const columnsFor = (isGlobal: boolean): ColSpec[] => [
  { key: "company", header: "Company", sort: "company", weight: isGlobal ? 180 : 170 },
  { key: "type", header: "Type", centered: true, weight: 58 },
  { key: "execution", header: "Execution", weight: 112 },
  { key: "cmp", header: "CMP", numeric: true, weight: isGlobal ? 86 : 62 },
  { key: "chg", header: "Chg%", tooltip: "Today's change %", numeric: true, weight: 54 },
  ...(isGlobal ? [] : [
    { key: "days", header: "Days", tooltip: "Days to trade 1% position @ 10% market volume", numeric: true, sort: "days" as const, weight: 54 },
  ]),
  { key: "decision", header: "Decision", centered: true, sort: "decision", weight: 84 },
  ...(isGlobal ? [
    { key: "prev", header: "Prev. Instr.", tooltip: "CIO instruction at the last session reset — hover a cell for the 3 most recent", weight: 130 },
  ] : []),
  ...(isGlobal
    ? [{ key: "sgs", header: "SGS", group: "holding" as const, numeric: true, weight: 56 }]
    : [
      { key: "pms", header: "PMS", group: "holding" as const, numeric: true, weight: 50 },
      { key: "aif", header: "AIF", group: "holding" as const, numeric: true, weight: 50 },
      { key: "ifsc", header: "IFSC", group: "holding" as const, numeric: true, weight: 50 },
    ]),
  ...(isGlobal ? [] : [
    { key: "last-order", header: "Last Order", group: "target" as const, tooltip: LAST_ORDER_TOOLTIP, numeric: true, weight: 66 },
  ]),
  { key: "analyst", header: "Analyst", group: "target", numeric: true, weight: 68 },
  { key: "ic", header: "IC", group: "target", numeric: true, weight: 66 },
  { key: "cio", header: "CIO", group: "target", numeric: true, weight: 72 },
  { key: "ir-analyst", header: "Analyst", group: "return", numeric: true, weight: 60 },
  { key: "ir-ic", header: "IC", group: "return", numeric: true, weight: 56 },
  { key: "ir-model", header: "Model", group: "return", numeric: true, weight: 60 },
  { key: "comment", header: "Comment", weight: isGlobal ? 160 : 140 },
];

const companyCount = (n: number) => `${n} ${n === 1 ? "company" : "companies"}`;

const SectionRow: React.FC<{ title: string; detail: string; span: number }> = ({ title, detail, span }) => (
  <tbody>
    <tr className={styles["section-row"]}>
      <td colSpan={span}>
        <span className={styles["section-title"]}>{title}</span>
        <span className={styles["section-detail"]}>{detail}</span>
      </td>
    </tr>
  </tbody>
);

interface OrderRecommendationTableProps {
  isGlobalTab: boolean;
  loading: boolean;
  /** A save or refetch is in flight over rows already shown: dim them rather than unmount. */
  refreshing: boolean;
  groupedByCompany: PendingOrder[][];
  filteredOmsRows: PendingOrder[];
  sortCol: SortCol | null;
  sortDir: "asc" | "desc";
  toggleSort: (col: SortCol) => void;
  isBulkEditing: boolean;
  bulkEditState: Record<number, BulkEditFields>;
  bulkEditSnapshot: Record<number, BulkEditFields>;
  bulkCreateState: Record<string, BulkCreateFields>;
  bulkCreateSnapshot: Record<string, BulkCreateFields>;
  handleBulkEditChange: (id: number, field: keyof BulkEditFields, value: BulkEditFields[keyof BulkEditFields]) => void;
  handleBulkCreateChange: (companyId: number, strategyGroup: "LHF" | "CORE", field: keyof BulkCreateFields, value: BulkCreateFields[keyof BulkCreateFields]) => void;
  setConfirmDialog: React.Dispatch<React.SetStateAction<ConfirmDialogState>>;
  saveErrors: { editIds: number[]; createKeys: string[] };
  lastOrderFilter: string;
  strategyFilter: StrategyFilter;
  riskByCompanyId: RiskByCompanyId;
  applyCioNote: (companyId: number, cioNote: string | null) => void;
}

/**
 * The Orders & Recommendations table. Hand-built rather than a DataTable: a
 * company's orders share real rowSpans and every row belongs on screen with
 * its siblings, so there is no pager — the body scrolls vertically under a
 * sticky two-row head. It scrolls horizontally only when the card is narrower
 * than the Σweights floor: in bulk edit always, in read mode on narrow screens
 * (phones, tablets), where the Company column also pins to the left.
 */
export const OrderRecommendationTable: React.FC<OrderRecommendationTableProps> = ({
  isGlobalTab,
  loading,
  refreshing,
  groupedByCompany,
  filteredOmsRows,
  sortCol,
  sortDir,
  toggleSort,
  isBulkEditing,
  bulkEditState,
  bulkEditSnapshot,
  bulkCreateState,
  bulkCreateSnapshot,
  handleBulkEditChange,
  handleBulkCreateChange,
  setConfirmDialog,
  saveErrors,
  lastOrderFilter,
  strategyFilter,
  riskByCompanyId,
  applyCioNote,
}) => {
  const columns = useMemo(() => columnsFor(isGlobalTab), [isGlobalTab]);
  const headerLayout = useMemo(() => layoutHeader(columns, GROUPS), [columns]);
  const totalWeight = useMemo(() => columns.reduce((sum, col) => sum + col.weight, 0), [columns]);
  const { tableRef, groupRowRef } = useGroupHeaderRow(!loading);

  // While the table is actively scrolling, suppress hover/tooltip work via a
  // CSS class toggled directly on the DOM (no React state → no re-renders).
  // Removed ~120ms after the last scroll event.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollIdleTimer = useRef<number | undefined>(undefined);
  const handleTableScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.classList.add(styles["is-scrolling"]);
    window.clearTimeout(scrollIdleTimer.current);
    scrollIdleTimer.current = window.setTimeout(
      () => el.classList.remove(styles["is-scrolling"]),
      120
    );
  }, []);
  useEffect(() => () => window.clearTimeout(scrollIdleTimer.current), []);

  if (loading) return <Placeholder kind="loading" message="Loading pending orders…" />;

  const renderHeaderCell = (col: ColSpec, rowSpan?: number) => {
    const { sort } = col;
    const isActive = sort != null && sortCol === sort;
    const className = [
      col.numeric ? styles.num : col.centered ? styles.center : "",
      col.sort ? shared["th-sortable"] : "",
      isActive ? shared["th-active"] : "",
    ].filter(Boolean).join(" ");
    return (
      <th
        key={col.key}
        rowSpan={rowSpan}
        className={className || undefined}
        onClick={sort ? () => toggleSort(sort) : undefined}
      >
        <HeaderLabel
          header={col.header}
          tooltip={col.tooltip}
          align={col.numeric || col.centered ? "center" : "left"}
          sortable={col.sort != null}
          active={isActive}
          sortDir={sortDir}
        />
      </th>
    );
  };

  return (
    <div className={styles["table-wrap"]}>
      {refreshing && <LinearProgress className={styles["refresh-bar"]} aria-label="Updating orders" />}
      <div
        className={`${styles["table-scroll"]} ${refreshing ? styles["is-refreshing"] : ""}`}
        ref={scrollRef}
        onScroll={handleTableScroll}
      >
        <table
          ref={tableRef}
          aria-busy={refreshing}
          className={`${shared.table} ${styles["rec-table"]} ${styles["rec-dense"]}`}
          // --table-floor is the same Σweights floor, applied by CSS on narrow screens.
          style={{ "--table-floor": `${totalWeight}px`, minWidth: isBulkEditing ? totalWeight : undefined } as React.CSSProperties}
        >
          <colgroup>
            {columns.map(col => (
              <col key={col.key} style={{ width: `${((col.weight / totalWeight) * 100).toFixed(3)}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr ref={groupRowRef}>
              {headerLayout.top.map(cell => (
                cell.kind === "column"
                  ? renderHeaderCell(cell.column, 2)
                  : <GroupHeaderCell key={cell.key} group={cell.group} span={cell.span} />
              ))}
            </tr>
            <tr className={shared["sub-header-row"]}>
              {headerLayout.sub.map(({ column }) => renderHeaderCell(column))}
            </tr>
          </thead>

          {groupedByCompany.length === 0 && filteredOmsRows.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={columns.length} className={styles["empty-cell"]}>
                  <Placeholder message="No matching orders or recommendations found." />
                </td>
              </tr>
            </tbody>
          )}

          <OrderTable
            groupedByCompany={groupedByCompany}
            isBulkEditing={isBulkEditing}
            bulkEditState={bulkEditState}
            bulkEditSnapshot={bulkEditSnapshot}
            bulkCreateState={bulkCreateState}
            bulkCreateSnapshot={bulkCreateSnapshot}
            handleBulkEditChange={handleBulkEditChange}
            handleBulkCreateChange={handleBulkCreateChange}
            setConfirmDialog={setConfirmDialog}
            errorEditIds={saveErrors.editIds}
            errorCreateKeys={saveErrors.createKeys}
            lastOrderFilter={lastOrderFilter}
            riskByCompanyId={riskByCompanyId}
            applyCioNote={applyCioNote}
          />

          {filteredOmsRows.length > 0 && (
            <SectionRow
              title="Recommendations"
              detail={`${companyCount(filteredOmsRows.length)} without an order`}
              span={columns.length}
            />
          )}
          <RecommendationTable
            filteredOmsRows={filteredOmsRows}
            isBulkEditing={isBulkEditing}
            bulkCreateState={bulkCreateState}
            bulkCreateSnapshot={bulkCreateSnapshot}
            handleBulkCreateChange={handleBulkCreateChange}
            strategyFilter={strategyFilter}
            errorCreateKeys={saveErrors.createKeys}
            riskByCompanyId={riskByCompanyId}
            applyCioNote={applyCioNote}
          />
        </table>
      </div>
    </div>
  );
};
